import { ArrowLeft, MoreHorizontal, Info } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import { useNavigate } from "react-router-dom";

const StressInputs = () => {
  const navigate = useNavigate();

  const signals = [
    {
      label: "HRV Deviation",
      value: "−12% vs baseline",
      status: "Elevated Load",
      statusColor: "bg-destructive/15 text-destructive",
      progress: 75,
      description:
        "Lower HRV relative to your baseline increases physiological load.",
      path: "/fitness2/hrv-deviation",
    },
    {
      label: "Sleep Recovery",
      value: "6.2 hrs",
      status: "Suboptimal",
      statusColor: "bg-warning/15 text-warning",
      progress: 55,
      description: "Sleep debt reduces recovery and increases stress load.",
      path: "/fitness2/sleep-recovery",
    },
    {
      label: "Resting Heart Rate Delta",
      value: "+5 bpm",
      status: "Elevated",
      statusColor: "bg-destructive/15 text-destructive",
      progress: 65,
      description: "Higher resting HR can indicate sympathetic activation.",
      path: "/fitness2/resting-hr",
    },
    {
      label: "Activity Recovery Buffer",
      value: "Low active recovery",
      status: "Neutral",
      statusColor: "bg-muted text-muted-foreground",
      progress: 35,
      description: "Light movement can modestly buffer physiological load.",
      path: "/fitness2/activity-recovery",
    },
  ];

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness2")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            Stress Index Inputs
          </h1>
          <Button variant="ghost" size="icon" className="rounded-xl">
            <MoreHorizontal className="h-5 w-5" />
          </Button>
        </div>

        {/* Confidence & Circadian */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="rounded-xl border-0 bg-accent/30 p-4 shadow-sm">
            <p className="mb-1 text-xs font-semibold text-destructive">
              Confidence
            </p>
            <p className="text-lg font-bold text-foreground">70%</p>
            <p className="text-[10px] text-muted-foreground">
              Modeled (partial signals)
            </p>
          </Card>
          <Card className="rounded-xl border-0 bg-accent/30 p-4 shadow-sm">
            <p className="mb-1 text-xs font-semibold text-muted-foreground">
              Circadian Window
            </p>
            <p className="text-lg font-bold text-foreground">Evening</p>
            <p className="text-[10px] text-muted-foreground">
              Reduced sensitivity window
            </p>
          </Card>
        </div>

        {/* Signal Breakdowns */}
        <div className="space-y-3">
          {signals.map((signal, i) => (
            <Card
              key={i}
              className="cursor-pointer rounded-xl border-0 p-4 shadow-sm transition-shadow hover:shadow-md active:scale-[0.99]"
              onClick={() => navigate(signal.path)}
            >
              <div className="mb-1 flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">
                  {signal.label}
                </p>
                <Badge
                  className={`${signal.statusColor} rounded-full border-0 text-[10px] font-semibold`}
                >
                  {signal.status}
                </Badge>
              </div>
              <p className="mb-2 text-xl font-bold text-foreground">
                {signal.value}
              </p>
              <Progress value={signal.progress} className="mb-2 h-2" />
              <p className="text-xs text-muted-foreground">
                {signal.description}
              </p>
            </Card>
          ))}
        </div>

        {/* Info Card */}
        <Card className="rounded-xl border-0 bg-muted/50 p-4 shadow-sm">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <p className="text-xs leading-relaxed text-muted-foreground">
              Your Stress Index is calculated from HRV deviation, sleep
              recovery, resting HR shift, and activity recovery. A circadian
              adjustment is applied (morning tends to be more metabolically
              tolerant than evening). Confidence reflects how complete and
              recent your signals are.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default StressInputs;
